/**
 * What a right-click on a card row acts on, when rows are also checkable.
 *
 * Pure, because the interesting part is the rule and `npm test` has no DOM — and because this one
 * decides how many notes a `Delete` deletes.
 *
 * `Cards.tsx` already answers the same question for the bulk bar: *"a bulk action applies to the
 * checked rows, or to the focused one when nothing is checked."* A pointer introduces a case that
 * rule has no answer for — **a right-click on a row that is not in the selection** — and getting
 * it wrong is how *Delete 14 notes* fires on a row you were pointing away from. Cards' delete is a
 * hard delete with no trash (PRD decision 3), so this is not a tidiness argument.
 */

export interface ContextTarget<T> {
  /** What the menu acts on, and what its labels must count. */
  rows: T[];
  /** Whether the caller should drop the checkboxes first, so the screen agrees with the menu. */
  clearSelection: boolean;
}

/**
 * Resolve the target, following the convention every file manager has taught:
 *
 * - **Right-clicking inside a selection acts on the selection.** You ticked fourteen rows in order
 *   to do something to fourteen rows.
 * - **Right-clicking outside one leaves it.** Pointing somewhere else is how you say you meant a
 *   different row, and acting on fourteen invisible notes because a checkbox was ticked further up
 *   the list is precisely the accident to design out. The selection is cleared as well as ignored,
 *   so the ticks on screen never disagree with what the menu says it will do.
 */
export function contextTarget<T>(
  selection: ReadonlySet<T>,
  row: T,
): ContextTarget<T> {
  if (selection.size === 0) return { rows: [row], clearSelection: false };
  if (selection.has(row)) return { rows: [...selection], clearSelection: false };
  return { rows: [row], clearSelection: true };
}
